import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitCpf } from "../lib/cpfApi";
import { Toast } from "../components/Toast";
import "./CpfPage.css";

export default function CpfPage() {
  const navigate = useNavigate();
  const [cpf, setCpf] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<
    { message: string; type?: "error" | "success" | "info" } | null
  >(null);

  const formatCpf = (value: string) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, "");

    // Aplica a máscara xxx.xxx.xxx-xx
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    } else if (numbers.length <= 9) {
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${
        numbers.slice(6)
      }`;
    } else {
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${
        numbers.slice(6, 9)
      }-${numbers.slice(9, 11)}`;
    }
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCpf(e.target.value);
    setCpf(formatted);
  };

  const validateCpf = (cpf: string): boolean => {
    // Remove caracteres não numéricos
    const cleanCpf = cpf.replace(/\D/g, "");

    // Verifica se tem 11 dígitos
    if (cleanCpf.length !== 11) {
      return false;
    }

    // Verifica se todos os dígitos são iguais (CPF inválido)
    if (/^(\d)\1{10}$/.test(cleanCpf)) {
      return false;
    }

    // Validação dos dígitos verificadores
    let sum = 0;
    let remainder;

    // Valida primeiro dígito
    for (let i = 1; i <= 9; i++) {
      sum += parseInt(cleanCpf.substring(i - 1, i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCpf.substring(9, 10))) return false;

    // Valida segundo dígito
    sum = 0;
    for (let i = 1; i <= 10; i++) {
      sum += parseInt(cleanCpf.substring(i - 1, i)) * (12 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCpf.substring(10, 11))) return false;

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar CPF
    if (!validateCpf(cpf)) {
      setToast({
        message: "CPF inválido. Por favor, verifique e tente novamente.",
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Remove a formatação antes de enviar
      const cleanCpf = cpf.replace(/\D/g, "");
      const response = await submitCpf({ cpf: cleanCpf });
      console.log("Resposta da API /checklist/cpf:", response);
      setToast({
        message: "CPF enviado com sucesso!",
        type: "success",
      });

      // Redirecionar para o checklist após 1.5 segundos
      setTimeout(() => {
        navigate("/checklist");
      }, 1500);
    } catch (error: any) {
      console.error("Erro ao enviar CPF:", error);
      setToast({
        message: error.message || "Erro ao enviar CPF",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cpf-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="cpf-container">
        <div className="cpf-header">
          <div className="cpf-header-content">
            <h1>📄 Informar CPF</h1>
            <p>Digite seu CPF para continuar</p>
          </div>
          <button
            className="back-button"
            onClick={() => navigate("/checklist")}
          >
            ← Voltar ao Checklist
          </button>
        </div>

        <div className="cpf-content">
          <form onSubmit={handleSubmit} className="cpf-form">
            <div className="form-group">
              <label htmlFor="cpf">CPF</label>
              <input
                type="text"
                id="cpf"
                value={cpf}
                onChange={handleCpfChange}
                placeholder="000.000.000-00"
                maxLength={14}
                required
                autoFocus
              />
              <span className="form-help">
                Digite apenas os números do seu CPF
              </span>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting || cpf.length < 14}
              >
                {isSubmitting ? "⏳ Enviando..." : "✓ Confirmar CPF"}
              </button>
            </div>
          </form>

          <div className="cpf-info">
            <div className="info-card">
              <h3>🔒 Segurança</h3>
              <p>
                Seus dados são tratados com total segurança e confidencialidade,
                em conformidade com a LGPD.
              </p>
            </div>
            <div className="info-card">
              <h3>ℹ️ Por que precisamos do CPF?</h3>
              <p>
                O CPF é necessário para validar sua identidade e garantir a
                segurança das suas transações na plataforma.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
